import { useState, useEffect } from 'react';
import axios from 'axios';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChartBar, CheckCircle, Warning, WarningCircle, Info } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CoverageIndicator = ({ projectId, scenesCount, onRefresh }) => {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCoverage = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/coverage`);
      setCoverage(res.data);
    } catch (err) {
      console.error('Failed to fetch coverage:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoverage();
  }, [projectId, scenesCount]);

  // Trigger refresh when onRefresh changes
  useEffect(() => {
    if (onRefresh) {
      fetchCoverage();
    }
  }, [onRefresh]);

  if (loading || !coverage) {
    return null;
  }

  // Don't show if no script
  if (coverage.status === 'no_script') {
    return null;
  }

  // Status colors and icons
  const getStatusConfig = (status, percentage) => {
    if (status === 'no_scenes') {
      return {
        color: '#A1A1AA',
        bgColor: '#333333',
        icon: Info,
        barColor: '#333333'
      };
    }
    if (percentage >= 90) {
      return {
        color: '#10B981',
        bgColor: '#10B98120',
        icon: CheckCircle,
        barColor: '#10B981'
      };
    }
    if (percentage >= 75) {
      return {
        color: '#F59E0B',
        bgColor: '#F59E0B20',
        icon: Warning,
        barColor: '#F59E0B'
      };
    }
    return {
      color: '#EF4444',
      bgColor: '#EF444420',
      icon: WarningCircle,
      barColor: '#EF4444'
    };
  };

  const config = getStatusConfig(coverage.status, coverage.percentage);
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-help transition-colors hover:bg-[#262626]"
            style={{ backgroundColor: config.bgColor }}
            data-testid="coverage-indicator"
          >
            <Icon size={16} weight="fill" style={{ color: config.color }} />
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: config.color }}>
                {coverage.percentage}%
              </span>
              {/* Mini progress bar */}
              <div className="w-16 h-1.5 bg-[#333333] rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${coverage.percentage}%`,
                    backgroundColor: config.barColor
                  }}
                />
              </div>
              <span className="text-xs text-[#A1A1AA]">covered</span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="bg-[#171717] border-[#333333] text-white p-4 max-w-xs"
        >
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <ChartBar size={18} weight="fill" style={{ color: config.color }} />
              <span className="font-semibold">Script Coverage</span>
            </div>
            
            {/* Coverage bar */}
            <div className="space-y-1">
              <div className="w-full h-2 bg-[#333333] rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${coverage.percentage}%`,
                    backgroundColor: config.barColor
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-[#A1A1AA]">
                <span>{coverage.percentage}% of script covered</span>
                <span>{coverage.scene_count} scenes</span>
              </div>
            </div>
            
            {/* Message */}
            <p className="text-sm text-[#A1A1AA]">{coverage.message}</p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-[#0a0a0a] p-2 rounded">
                <div className="text-[#A1A1AA]">Images</div>
                <div className="text-white font-medium">{coverage.scenes_with_images}/{coverage.scene_count}</div>
              </div>
              <div className="bg-[#0a0a0a] p-2 rounded">
                <div className="text-[#A1A1AA]">Duration</div>
                <div className="text-white font-medium">{Math.floor(coverage.total_duration / 60)}:{String(coverage.total_duration % 60).padStart(2, '0')}</div>
              </div>
            </div>
            
            {/* Uncovered sections warning */}
            {coverage.uncovered_sections && coverage.uncovered_sections.length > 0 && (
              <div className="border-t border-[#333333] pt-2">
                <div className="text-xs text-[#F59E0B] flex items-center gap-1 mb-1">
                  <Warning size={12} weight="fill" />
                  {coverage.uncovered_sections.length} section(s) may need scenes
                </div>
                <div className="text-xs text-[#A1A1AA] max-h-20 overflow-y-auto space-y-1">
                  {coverage.uncovered_sections.slice(0, 3).map((section, i) => (
                    <div key={i} className="truncate">
                      • {section.preview}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
